/**
 * Memoised recipe calculation.
 *
 * PLAN §10 sets the budget at **under 100 ms from keystroke to vitals**. The
 * engine itself runs in low single-digit milliseconds, so the budget is met
 * with room to spare even uncached; this cache exists so that the *other*
 * components re-rendering for an unrelated reason (a route change, a theme
 * flip, a popover opening) do not each re-run it.
 *
 * The key is the recipe document's own identity. `applyCommand` rebuilds
 * exactly the documents it touched and shares the rest by reference, so
 * "document unchanged" is `===` and a `WeakMap` both hits reliably and lets the
 * entry go when the document does.
 */

import type { EquipmentProfileDoc, RecipeDoc, WorkspaceState } from '@beerwright/commands';
import { resolveEquipmentProfileDoc, toCalculationInputs } from '@beerwright/commands';
import type { RecipeCalculation } from '@beerwright/core';
import { calculateRecipe } from '@beerwright/core';

interface CacheEntry {
  readonly equipment: EquipmentProfileDoc;
  readonly calculation: RecipeCalculation;
}

const CACHE = new WeakMap<RecipeDoc, CacheEntry>();

/**
 * Calculate a recipe against a profile, reusing the last result when neither
 * document has changed.
 */
export function calculationFor(doc: RecipeDoc, equipment: EquipmentProfileDoc): RecipeCalculation {
  const cached = CACHE.get(doc);
  if (cached !== undefined && cached.equipment === equipment) return cached.calculation;
  const { recipe, equipment: profile } = toCalculationInputs(doc, equipment);
  const calculation = calculateRecipe(recipe, profile);
  CACHE.set(doc, { equipment, calculation });
  return calculation;
}

/**
 * Calculate a recipe in a workspace, resolving its equipment profile the way
 * the engine bridge does (recipe's own → workspace default → first built-in).
 */
export function calculationInWorkspace(
  state: WorkspaceState,
  doc: RecipeDoc,
): RecipeCalculation | null {
  const equipment = resolveEquipmentProfileDoc(state, doc);
  if (equipment === undefined) return null;
  return calculationFor(doc, equipment);
}
